import { useEffect, useState, useRef } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Save, Upload, X, Image as ImageIcon, Stamp } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [brandingBust, setBrandingBust] = useState(Date.now());
  const [previews, setPreviews] = useState({ logo: null, seal: null });
  const logoRef = useRef(null);
  const sealRef = useRef(null);

  useEffect(() => {
    api.get("/settings")
      .then((res) => setForm(res.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!form) return;
    ["logo", "seal"].forEach(async (kind) => {
      if (form[`${kind}_path`]) {
        try {
          const r = await api.get(`/settings/branding/${kind}`, { responseType: "blob" });
          setPreviews((p) => ({ ...p, [kind]: URL.createObjectURL(r.data) }));
        } catch { setPreviews((p) => ({ ...p, [kind]: null })); }
      } else {
        setPreviews((p) => ({ ...p, [kind]: null }));
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form?.logo_path, form?.seal_path, brandingBust]);

  const uploadBranding = async (kind, file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(`/settings/branding/${kind}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const res = await api.get("/settings");
      setForm(res.data);
      setBrandingBust(Date.now());
      toast.success(kind === "logo" ? "ロゴを保存しました" : "社印を保存しました");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const deleteBranding = async (kind) => {
    try {
      await api.delete(`/settings/branding/${kind}`);
      const res = await api.get("/settings");
      setForm(res.data);
      setBrandingBust(Date.now());
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", {
        company_name: form.company_name || "",
        address: form.address || "",
        phone: form.phone || "",
        email: form.email || "",
        tax_id: form.tax_id || "",
        invoice_registration_number: form.invoice_registration_number || "",
        fiscal_year_start: form.fiscal_year_start || "04-01",
        invoice_prefix: form.invoice_prefix || "INV",
        bank_info: form.bank_info || "",
      });
      toast.success("設定を保存しました");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
    setSaving(false);
  };

  if (loading || !form) {
    return (
      <div className="flex items-center justify-center py-24">
        <Loader2 className="h-6 w-6 animate-spin text-emerald-900" />
      </div>
    );
  }

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  return (
    <div className="space-y-6 max-w-3xl" data-testid="settings-root">
      <div>
        <div className="text-xs uppercase tracking-[0.2em] font-semibold text-emerald-800">設定</div>
        <h1 className="mt-1 font-display font-bold text-3xl tracking-tight text-stone-900">事業情報・設定</h1>
        <p className="mt-1 text-sm text-stone-500">請求書や帳票に表示される情報を管理します。</p>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-6 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <Label className="text-sm">会社名・屋号</Label>
            <Input value={form.company_name || ""} onChange={set("company_name")} className="mt-1.5" data-testid="settings-company-input" />
          </div>
          <div>
            <Label className="text-sm">電話番号</Label>
            <Input value={form.phone || ""} onChange={set("phone")} className="mt-1.5" data-testid="settings-phone-input" />
          </div>
          <div>
            <Label className="text-sm">メールアドレス</Label>
            <Input value={form.email || ""} onChange={set("email")} className="mt-1.5" data-testid="settings-email-input" />
          </div>
          <div className="col-span-2">
            <Label className="text-sm">住所</Label>
            <Input value={form.address || ""} onChange={set("address")} className="mt-1.5" data-testid="settings-address-input" />
          </div>
          <div>
            <Label className="text-sm">法人番号・個人番号</Label>
            <Input value={form.tax_id || ""} onChange={set("tax_id")} className="mt-1.5" data-testid="settings-taxid-input" placeholder="1234567890123" />
          </div>
          <div>
            <Label className="text-sm">適格請求書 登録番号</Label>
            <Input value={form.invoice_registration_number || ""} onChange={set("invoice_registration_number")} className="mt-1.5" data-testid="settings-reginum-input" placeholder="T1234567890123" />
          </div>
          <div>
            <Label className="text-sm">会計年度開始 (MM-DD)</Label>
            <Input value={form.fiscal_year_start || ""} onChange={set("fiscal_year_start")} className="mt-1.5" data-testid="settings-fiscal-input" />
          </div>
          <div>
            <Label className="text-sm">請求書プレフィックス</Label>
            <Input value={form.invoice_prefix || ""} onChange={set("invoice_prefix")} className="mt-1.5" data-testid="settings-prefix-input" />
          </div>
          <div className="col-span-2">
            <Label className="text-sm">振込先情報</Label>
            <Input value={form.bank_info || ""} onChange={set("bank_info")} className="mt-1.5" data-testid="settings-bank-input" placeholder="例: ○○銀行 普通 1234567" />
          </div>
        </div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-6 space-y-4" data-testid="settings-branding">
        <div>
          <h2 className="font-display font-semibold text-lg text-stone-900">ロゴ・社印</h2>
          <p className="text-sm text-stone-500 mt-0.5">請求書などの帳票PDFと、試算表・P/L・B/S等の帳票出力に印字されます。(PNG / JPEG / WEBP)</p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
          <BrandingSlot
            label="会社ロゴ" icon={<ImageIcon className="h-4 w-4" />} preview={previews.logo}
            inputRef={logoRef} onPick={(f) => uploadBranding("logo", f)} onRemove={() => deleteBranding("logo")}
            hasImage={!!form.logo_path} testid="logo"
          />
          <BrandingSlot
            label="社印 (角印・電子印)" icon={<Stamp className="h-4 w-4" />} preview={previews.seal}
            inputRef={sealRef} onPick={(f) => uploadBranding("seal", f)} onRemove={() => deleteBranding("seal")}
            hasImage={!!form.seal_path} testid="seal"
          />
        </div>
      </div>

      <div className="rounded-xl border border-stone-200 bg-white p-6">
        <div>
          <button
            onClick={save}
            disabled={saving}
            data-testid="settings-save-button"
            className="inline-flex items-center gap-2 rounded-md bg-emerald-900 px-5 py-2.5 text-sm font-medium text-white hover:bg-emerald-950 disabled:opacity-60"
          >
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
            設定を保存
          </button>
        </div>
      </div>
    </div>
  );
}

function BrandingSlot({ label, icon, preview, inputRef, onPick, onRemove, hasImage, testid }) {
  return (
    <div>
      <Label className="text-sm flex items-center gap-1.5">{icon} {label}</Label>
      <div className="mt-2 flex items-center gap-4">
        <div className="h-24 w-24 rounded-lg border border-dashed border-stone-300 bg-stone-50 flex items-center justify-center overflow-hidden">
          {preview ? (
            <img src={preview} alt={label} className="max-h-full max-w-full object-contain" data-testid={`branding-preview-${testid}`} />
          ) : (
            <span className="text-[11px] text-stone-400">未設定</span>
          )}
        </div>
        <div className="flex flex-col gap-2">
          <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden"
            onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; onPick(f); }}
            data-testid={`branding-input-${testid}`} />
          <button onClick={() => inputRef.current?.click()} className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 px-3 py-1.5 text-sm text-stone-700 hover:bg-stone-50" data-testid={`branding-upload-${testid}`}>
            <Upload className="h-3.5 w-3.5" /> {hasImage ? "変更" : "アップロード"}
          </button>
          {hasImage && (
            <button onClick={onRemove} className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm text-red-600 hover:bg-red-50" data-testid={`branding-remove-${testid}`}>
              <X className="h-3.5 w-3.5" /> 削除
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
